import { useEffect } from 'react'
import { useNow } from '@/hooks/useNow'
import { Link } from '@tanstack/react-router'
import { useResource } from '@/db/hooks'
import type { TaskSpec, TaskStatus } from '@/kinds/task/schema'
import type { TaskResource } from '@/kinds/task/apply'
import { taskActions } from '@/lib/actions'
import { fmtElapsed, fmtMinutes } from '@/lib/time'
import { useUi } from '@/state/ui'
import { Button } from './ui'

/** Persistent bar on every view while a task is active (spec §8.4). */
export const ActiveBar = () => {
  const activeId = useUi((s) => s.activeTaskId)
  const flash = useUi((s) => s.flash)
  const task = useResource<TaskSpec, TaskStatus>(activeId ?? undefined) as TaskResource | undefined
  const running = !!task?.status.started_at && !task.deleted_at
  const now = useNow(running ? 1000 : undefined)
  useEffect(() => {
    // the task was stopped/completed elsewhere (another device): drop the bar
    if (activeId && task && (!task.status.started_at || task.deleted_at)) useUi.getState().setActiveTask(null)
  }, [activeId, task])

  if (flash && !running) {
    return (
      <div
        className="sticky top-0 z-30 bg-emerald-950/90 backdrop-blur border-b border-emerald-900 text-emerald-100 text-sm px-4 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:pt-2 text-center fade-in"
        role="status"
      >
        {flash.text}
        {flash.action && (
          <button
            type="button"
            onClick={() => {
              const run = flash.action!.run
              useUi.getState().showFlash('', 1)
              run()
            }}
            className="ml-3 underline underline-offset-2 font-medium"
            data-testid="flash-action"
          >
            {flash.action.label}
          </button>
        )}
      </div>
    )
  }
  // nothing to show: still clear the notch / dynamic island on phones
  if (!running || !task) return <div className="md:hidden h-[env(safe-area-inset-top)]" aria-hidden />
  const elapsed = fmtElapsed(task.status.started_at!, now)
  const est = task.spec.estimate_minutes
  const over = est && (now - new Date(task.status.started_at!).getTime()) / 60_000 > est
  return (
    <div
      className="sticky top-0 z-30 bg-raised/95 backdrop-blur-md border-b border-line px-3 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:pt-2 flex items-center gap-3 shadow-[0_8px_24px_-16px_var(--color-accent)]"
      data-testid="active-bar"
    >
      <Link to="/tasks/$id" params={{ id: task.id }} className="flex-1 min-w-0">
        <div className="truncate font-medium">{task.spec.title}</div>
        <div className={`text-xs tabular-nums ${over ? 'text-amber-300' : 'text-muted'}`}>
          {elapsed}
          {est ? ` · est ${fmtMinutes(est)}` : ''}
          {task.status.actual_minutes ? ` · earlier ${fmtMinutes(task.status.actual_minutes)}` : ''}
        </div>
      </Link>
      <Button onClick={() => void taskActions.stop(task)}>Stop</Button>
      <Button variant="primary" onClick={() => void taskActions.complete(task)}>
        Complete
      </Button>
    </div>
  )
}
